import { type PointerEvent as ReactPointerEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AppSidebar } from './AppSidebar'
import { DashboardHeader } from './DashboardHeader'
import { StoreSyncAddedToasts } from './StoreSyncAddedToasts'
import { RenewalBanner } from '@/components/billing/RenewalBanner'
import { APP_VERSION } from '@/lib/app-version'
import { cn } from '@/lib/utils'
import { MpWorkspaceFrame } from '@/ui/components'

interface DashboardLayoutProps {
  children?: ReactNode
  navigationOrgId?: string | null
}

const SIDEBAR_WIDTH_STORAGE_KEY = 'mphelper.dashboard.sidebar.width'
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'mphelper.dashboard.sidebar.collapsed'
const SIDEBAR_DEFAULT_WIDTH = 240
const SIDEBAR_MIN_WIDTH = 216
const SIDEBAR_MAX_WIDTH = 340
const SIDEBAR_COLLAPSED_WIDTH = 64
const SIDEBAR_COLLAPSE_THRESHOLD = 172
const SIDEBAR_CLICK_TOLERANCE = 4

function readStoredSidebarWidth() {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH
  const value = Number(window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY))
  if (!Number.isFinite(value)) return SIDEBAR_DEFAULT_WIDTH
  return Math.min(Math.max(value, SIDEBAR_MIN_WIDTH), SIDEBAR_MAX_WIDTH)
}

function readStoredSidebarCollapsed() {
  if (typeof window === 'undefined') return true
  const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY)
  if (stored === null) return true
  return stored === 'true'
}

export function DashboardLayout({ children, navigationOrgId }: DashboardLayoutProps) {
  const currentYear = new Date().getFullYear()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarWidth, setSidebarWidth] = useState(readStoredSidebarWidth)
  const [isCollapsed, setIsCollapsed] = useState(readStoredSidebarCollapsed)
  const [isResizingSidebar, setIsResizingSidebar] = useState(false)
  const sidebarDragRef = useRef<{
    startX: number
    startWidth: number
    startedCollapsed: boolean
    hasMoved: boolean
  } | null>(null)
  const location = useLocation()
  const isWidePage =
    location.pathname.endsWith('/categories') ||
    location.pathname.endsWith('/king-of-marketplaces') ||
    location.pathname.endsWith('/product-visibility') ||
    location.pathname.endsWith('/products') ||
    location.pathname.endsWith('/competitors') ||
    location.pathname.endsWith('/help') ||
    location.pathname.endsWith('/copilot') ||
    /\/products\/[^/]+\/[^/]+$/.test(location.pathname) ||
    /\/competitors\/[^/]+\/[^/]+$/.test(location.pathname)

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth))
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(isCollapsed))
  }, [isCollapsed, sidebarWidth])

  useEffect(() => {
    if (!isResizingSidebar) return

    function handlePointerMove(event: globalThis.PointerEvent) {
      if (!sidebarDragRef.current) return

      const drag = sidebarDragRef.current
      const deltaX = event.clientX - drag.startX
      if (Math.abs(deltaX) > SIDEBAR_CLICK_TOLERANCE) {
        drag.hasMoved = true
      }

      const nextWidth = drag.startWidth + deltaX
      if (nextWidth <= SIDEBAR_COLLAPSE_THRESHOLD) {
        setIsCollapsed(true)
        return
      }

      setIsCollapsed(false)
      setSidebarWidth(Math.min(Math.max(nextWidth, SIDEBAR_MIN_WIDTH), SIDEBAR_MAX_WIDTH))
    }

    function handlePointerUp() {
      const drag = sidebarDragRef.current
      if (drag?.startedCollapsed && !drag.hasMoved) {
        setIsCollapsed(false)
      }

      sidebarDragRef.current = null
      setIsResizingSidebar(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizingSidebar])

  function startSidebarResize(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return
    event.preventDefault()
    sidebarDragRef.current = {
      startX: event.clientX,
      startWidth: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth,
      startedCollapsed: isCollapsed,
      hasMoved: false,
    }
    setIsResizingSidebar(true)
  }

  const desktopSidebarWidth = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth

  return (
    <div className="flex h-svh min-h-0 flex-col overflow-hidden bg-[var(--mph-bg-app)] text-[var(--mph-text-primary)] lg:flex-row">
      {/* Desktop sidebar */}
      <div className={cn(
        "relative hidden h-svh shrink-0 overflow-visible lg:block",
        !isResizingSidebar && "transition-[width] duration-200",
      )}
        style={{ width: desktopSidebarWidth }}
      >
        <AppSidebar 
          isCollapsed={isCollapsed}
          onCollapse={() => setIsCollapsed(true)}
          onToggleCollapsed={() => setIsCollapsed((current) => !current)}
          organizationIdOverride={navigationOrgId}
        />
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Змінити ширину навігації"
          aria-valuemin={isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_MIN_WIDTH}
          aria-valuemax={SIDEBAR_MAX_WIDTH}
          aria-valuenow={desktopSidebarWidth}
          tabIndex={0}
          title={isCollapsed ? 'Розгорнути меню' : 'Потягніть, щоб змінити ширину.'}
          onPointerDown={startSidebarResize}
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
            event.preventDefault()
            setIsCollapsed(false)
            setSidebarWidth((width) => Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH,
              event.key === 'Home' ? SIDEBAR_MIN_WIDTH : event.key === 'End' ? SIDEBAR_MAX_WIDTH : width + (event.key === 'ArrowRight' ? 10 : -10))))
          }}
          className="group absolute -right-0.5 top-0 z-30 flex h-full w-1.5 cursor-col-resize touch-none items-center justify-center focus-visible:outline-none"
        >
          <span aria-hidden="true" className={cn('flex h-full w-0.5 items-center justify-center bg-[var(--mph-border-subtle)] group-hover:bg-[var(--mph-accent-soft)] group-focus-visible:bg-[var(--mph-accent-soft)]', isResizingSidebar && 'bg-[var(--mph-accent-soft)]')}>
            <span className="h-8 w-px rounded bg-[var(--mph-text-tertiary)] opacity-40" />
          </span>
        </div>
      </div>

      {/* Mobile sidebar (slide-down simple variant) */}
      <div className="shrink-0 w-full lg:hidden">
        <DashboardHeader onToggleSidebar={() => setSidebarOpen((open) => !open)} />
        {sidebarOpen && (
          <div className="border-b">
            <AppSidebar isMobile onNavigate={() => setSidebarOpen(false)} organizationIdOverride={navigationOrgId} />
          </div>
        )}
      </div>

      {/* Main area */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-2 lg:h-svh lg:p-3 lg:pl-2">
        <MpWorkspaceFrame className="h-full min-h-0 rounded-[16px]">
          {/* Desktop header */}
          <div className="hidden lg:block">
            <DashboardHeader />
          </div>

          <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]">
            <div className="flex min-h-full flex-col">
              <div
                className={cn(
                  "mx-auto w-full flex-1",
                  location.pathname.endsWith('/categories')
                    ? "pl-2 pr-4 py-2 sm:pr-5 lg:pl-3 lg:pr-6"
                    : "px-4 py-4 sm:px-5 lg:px-6 lg:py-5",
                  isWidePage ? "max-w-none" : "max-w-7xl",
                )}
              >
                <RenewalBanner />
                {children || <Outlet />}
              </div>
              <footer className="flex shrink-0 flex-col gap-1 border-t border-[var(--mph-border-subtle)] bg-[var(--mph-bg-workspace)]/78 px-4 py-2 text-[11px] text-[var(--mph-text-tertiary)] sm:flex-row sm:items-center sm:justify-between lg:px-6">
                <span>© {currentYear} Маркетплейс Помічник.</span>
                <span className="font-medium tabular-nums">{APP_VERSION}</span>
              </footer>
            </div>
          </main>
        </MpWorkspaceFrame>
      </div>
      <Toaster richColors />
      <StoreSyncAddedToasts />
    </div>
  )
}
